/**
 * React Query hooks for investor portal documents
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { portalQueryKeys } from '@/portal/shared/hooks/queryKeys';
import { errorMessage, portalLog } from '@/portal/shared/utils/portal-logger';
import {
  DocumentsService,
  type PortalDocumentsResponse,
} from '../services/documents-service';

/**
 * Hook to fetch all documents across investor's funds
 */
export const useDocuments = () => {
  return useApiQuery<PortalDocumentsResponse>(
    portalQueryKeys.documents.list(),
    () => DocumentsService.getAllDocuments(),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
    }
  );
};

/**
 * Hook to handle document downloads
 *
 * Uses DocumentsService.downloadDocument which reuses the admin download logic
 */
export const useDocumentDownload = () => {
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const downloadDocument = useCallback(
    async (documentId: string, filename: string) => {
      setIsDownloading(documentId);
      setError(null);

      try {
        await DocumentsService.downloadDocument(documentId, filename);
        void queryClient.invalidateQueries({
          queryKey: portalQueryKeys.documents.list(),
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to download document'
        );
        portalLog.error('portal.document.download.failed', {
          error_message: errorMessage(err),
          document_id: documentId,
        });
      } finally {
        setIsDownloading(null);
      }
    },
    [queryClient]
  );

  return {
    downloadDocument,
    isDownloading,
    error,
    clearError: () => setError(null),
  };
};
